import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Composable for character counter logic
 * Always call unconditionally in setup — returns safe defaults when maxLength is null.
 * @param {import('vue').Ref<number>|import('vue').ComputedRef<number>} currentLength - Current character count (ref or computed)
 * @param {import('vue').Ref<number>|number|null} maxLength - Maximum allowed characters (null = disabled)
 * @returns {Object} Counter state and display properties
 */
export function useCharacterCounter(currentLength, maxLength) {
  const { t } = useI18n()

  const maxRef = computed(() => unref(maxLength))

  const charsLeft = computed(() => {
    if (maxRef.value == null) return 0
    return maxRef.value - unref(currentLength)
  })

  const isOverLimit = computed(() => maxRef.value != null && charsLeft.value < 0)

  const counterText = computed(() => {
    if (maxRef.value == null) return ''
    if (isOverLimit.value) {
      return t('form.counter.over_limit', { n: Math.abs(charsLeft.value), max: maxRef.value })
    }
    return t('form.counter.characters_left', { n: charsLeft.value, max: maxRef.value })
  })

  const counterColorClass = computed(() => isOverLimit.value ? 'text-error' : 'text-neutral500')

  return {
    charsLeft,
    isOverLimit,
    counterText,
    counterColorClass,
  }
}
